import React, { useState, useEffect, Fragment } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import { toast } from 'react-toastify';
import { COMPANY_ROLES } from '@utils/constants';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import ConfirmModal from '@components/shared/ConfirmModal';
import Pagination from '@components/dashboard/admin/shared/Pagination';

const USERS_PAGE_SIZE = 20;

// Seitenweise: der Server liefert höchstens USERS_PAGE_SIZE pro Abruf.
const companyUsersQuery = (companyId, page = 1) => ({
  queryKey: ['companyUsers', companyId, 'page', page],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}/users`, {
      params: { page, limit: USERS_PAGE_SIZE },
    });
    return data;
  },
});

const invitationsQuery = (companyId) => ({
  queryKey: ['invitations', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(
      `/companies/${companyId}/invitations`
    );
    return data;
  },
});

export const loader = (queryClient) => async () => {
  const userData = await queryClient.ensureQueryData({
    queryKey: ['user'],
    queryFn: async () => {
      const { data } = await customFetch.get('/users/current-user');
      return data;
    },
  });
  const companyId = userData.user.company;
  if (!companyId) return { users: [] };
  await queryClient.ensureQueryData(companyUsersQuery(companyId));
  return { companyId };
};

const CompanyUsers = () => {
  const { user } = useDashboardContext();
  const companyId = user.company;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    ...companyUsersQuery(companyId, page),
    keepPreviousData: true,
  });
  const users = data?.users || [];
  const totalPages = data?.totalPages ?? 0;
  // Nach dem Entfernen des letzten Mitarbeiters einer Seite zurückblättern.
  useEffect(() => {
    if (totalPages && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const { data: invitationsData, isLoading: invitationsLoading } = useQuery({
    ...invitationsQuery(companyId),
    enabled: !!companyId,
  });
  const invitations = invitationsData?.invitations || [];

  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  // true = Benutzer direkt anlegen (Variante C), false = klassisch einladen
  const [createMode, setCreateMode] = useState(false);

  const openPanel = (mode) => {
    setCreateMode(mode === 'create');
    setShowInvite(true);
  };

  // ConfirmModal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    loading: false,
  });

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ open: true, title, message, onConfirm, loading: false });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, open: false }));
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const companyRole = formData.get('companyRole');

    setInviting(true);
    try {
      await customFetch.post(`/companies/${companyId}/users/invite`, {
        email,
        companyRole,
      });
      queryClient.invalidateQueries(['companyUsers', companyId]);
      queryClient.invalidateQueries(['invitations', companyId]);
      toast.success('Einladung wurde versendet');
      setShowInvite(false);
      e.target.reset();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Einladen');
    } finally {
      setInviting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const payload = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      title: formData.get('title') || undefined,
      companyRole: formData.get('companyRole'),
      sendMail: formData.get('sendMail') === 'on',
    };

    setInviting(true);
    try {
      const { data } = await customFetch.post(
        `/companies/${companyId}/users/create`,
        payload
      );
      queryClient.invalidateQueries(['companyUsers', companyId]);
      toast.success(data?.msg || 'Benutzer erstellt');
      setShowInvite(false);
      e.target.reset();
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Erstellen');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveUser = (userId, userName) => {
    openConfirm(
      'Benutzer entfernen',
      `${userName} wirklich aus der Firma entfernen?`,
      async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }));
        try {
          await customFetch.delete(`/companies/${companyId}/users/${userId}`);
          queryClient.invalidateQueries(['companyUsers', companyId]);
          toast.success('Benutzer entfernt');
        } catch (error) {
          toast.error(error?.response?.data?.msg || 'Fehler beim Entfernen');
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await customFetch.patch(`/companies/${companyId}/users/${userId}/role`, {
        companyRole: newRole,
      });
      queryClient.invalidateQueries(['companyUsers', companyId]);
      toast.success('Rolle aktualisiert');
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Ändern der Rolle');
    }
  };

  const handleCancelInvitation = (invitationId, email) => {
    openConfirm(
      'Einladung zurückziehen',
      `Einladung an ${email} wirklich zurückziehen?`,
      async () => {
        setConfirmModal((prev) => ({ ...prev, loading: true }));
        try {
          await customFetch.delete(
            `/companies/${companyId}/invitations/${invitationId}`
          );
          queryClient.invalidateQueries(['invitations', companyId]);
          toast.success('Einladung zurückgezogen');
        } catch (error) {
          toast.error(
            error?.response?.data?.msg ||
              'Fehler beim Zurückziehen der Einladung'
          );
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const handleResendInvitation = async (email) => {
    try {
      await customFetch.post(`/companies/${companyId}/users/invite`, { email });
      queryClient.invalidateQueries(['invitations', companyId]);
      toast.success(`Einladung erneut an ${email} gesendet`);
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim erneuten Senden');
    }
  };

  return (
    <>
      {/* ConfirmModal */}
      <ConfirmModal
        open={confirmModal.open}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Bestätigen"
        loading={confirmModal.loading}
      />

      {/* Invite SlideIn */}
      <Transition.Root show={showInvite} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setShowInvite}>
          <div className="fixed inset-0" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <form
                      onSubmit={createMode ? handleCreate : handleInvite}
                      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
                    >
                      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
                        <div className="px-4 sm:px-6">
                          <div className="flex items-start justify-between">
                            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                              {createMode
                                ? 'Benutzer direkt erstellen'
                                : 'Mitarbeiter einladen'}
                            </Dialog.Title>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500"
                                onClick={() => setShowInvite(false)}
                              >
                                <span className="absolute -inset-2.5" />
                                <XMarkIcon className="h-6 w-6" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="relative mt-6 flex-1 px-4 sm:px-6">
                          <div className="space-y-6 pt-6">
                            {createMode && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label
                                    htmlFor="firstName"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Vorname *
                                  </label>
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="firstName"
                                      id="firstName"
                                      required
                                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label
                                    htmlFor="lastName"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Nachname *
                                  </label>
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="lastName"
                                      id="lastName"
                                      required
                                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            <div>
                              <label
                                htmlFor="email"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                E-Mail-Adresse *
                              </label>
                              <div className="mt-2">
                                <input
                                  type="email"
                                  name="email"
                                  id="email"
                                  required
                                  placeholder="mitarbeiter@firma.ch"
                                  className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                              <p className="mt-1 text-sm text-gray-500">
                                {createMode
                                  ? 'Der Benutzer wird sofort angelegt. Du kannst Karte & Links direkt bearbeiten.'
                                  : 'Eine Einladung wird per E-Mail versendet.'}
                              </p>
                            </div>
                            {createMode && (
                              <div>
                                <label
                                  htmlFor="title"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  Titel / Position
                                </label>
                                <div className="mt-2">
                                  <input
                                    type="text"
                                    name="title"
                                    id="title"
                                    placeholder="z.B. Sales Manager"
                                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                  />
                                </div>
                              </div>
                            )}
                            <div>
                              <label
                                htmlFor="companyRole"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Rolle
                              </label>
                              <div className="mt-2">
                                <select
                                  name="companyRole"
                                  id="companyRole"
                                  className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                >
                                  <option value={COMPANY_ROLES.MEMBER}>
                                    Mitglied
                                  </option>
                                  <option value={COMPANY_ROLES.COMPANY_ADMIN}>
                                    Firmen-Admin
                                  </option>
                                </select>
                              </div>
                            </div>
                            {createMode && (
                              <div className="relative flex items-start">
                                <div className="flex h-6 items-center">
                                  <input
                                    id="sendMail"
                                    name="sendMail"
                                    type="checkbox"
                                    defaultChecked
                                    className="h-4 w-4 rounded border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                                  />
                                </div>
                                <div className="ml-3 text-sm leading-6">
                                  <label
                                    htmlFor="sendMail"
                                    className="font-medium text-gray-900"
                                  >
                                    Aktivierungs-E-Mail senden
                                  </label>
                                  <p className="text-gray-500">
                                    Der Benutzer erhält einen Link, um ein
                                    Passwort zu setzen und sich selbst
                                    anzumelden. Ohne diese Option bleibt es ein
                                    rein verwaltetes Profil.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 justify-end px-4 py-4">
                        <button
                          type="button"
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                          onClick={() => setShowInvite(false)}
                        >
                          Abbrechen
                        </button>
                        <button
                          type="submit"
                          disabled={inviting}
                          className="ml-4 inline-flex justify-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {inviting
                            ? createMode
                              ? 'Wird erstellt...'
                              : 'Wird gesendet...'
                            : createMode
                            ? 'Benutzer erstellen'
                            : 'Einladung senden'}
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Mitarbeiter{data?.total ? ` (${data.total})` : ''}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Verwalte die Mitarbeiter deiner Organisation.
            </p>
          </div>
          <div className="mt-4 flex gap-3 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={() => openPanel('invite')}
              className="flex items-center rounded-md bg-white px-3 py-2 text-center text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Einladen
            </button>
            <button
              type="button"
              onClick={() => openPanel('create')}
              className="flex items-center rounded-md bg-svisit-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Benutzer erstellen
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              {isLoading ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  Laden...
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  Noch keine Mitarbeiter in der Organisation.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">
                        Vorname
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Nachname
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        E-Mail
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Titel
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Rolle
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                        <span className="sr-only">Aktionen</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {users.map((member) => (
                      <tr key={member._id} className="even:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                          {member.firstName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          {member.lastName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {member.email}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {member.title || '–'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <select
                            value={member.companyRole || COMPANY_ROLES.MEMBER}
                            onChange={(e) =>
                              handleRoleChange(member._id, e.target.value)
                            }
                            disabled={String(member._id) === String(user._id)}
                            className="rounded-md border-0 py-1 pl-2 pr-8 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-svisit-primary-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value={COMPANY_ROLES.MEMBER}>
                              Mitglied
                            </option>
                            <option value={COMPANY_ROLES.COMPANY_ADMIN}>
                              Admin
                            </option>
                          </select>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {member.isVerified ? (
                            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              <CheckIcon className="h-3 w-3 mr-1" />
                              Verifiziert
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                              Ausstehend
                            </span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                          <div className="flex justify-end gap-3">
                            {String(member._id) !== String(user._id) && (
                              <>
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/dashboard/firma/mitarbeiter/${member._id}`
                                    )
                                  }
                                  className="text-svisit-primary-600 hover:text-svisit-primary-900"
                                  title="Bearbeiten"
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleRemoveUser(
                                      member._id,
                                      `${member.firstName} ${member.lastName}`
                                    )
                                  }
                                  className="text-red-600 hover:text-red-900"
                                  title="Entfernen"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination
                currentPage={page}
                numOfPages={totalPages}
                total={data?.total ?? 0}
                limit={USERS_PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>

        {/* Pending Invitations */}
        {!invitationsLoading && invitations.length > 0 && (
          <div className="mt-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900 mb-4">
              Offene Einladungen ({invitations.length})
            </h2>
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">
                        E-Mail
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Eingeladen von
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Läuft ab
                      </th>
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                        <span className="sr-only">Aktionen</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {invitations.map((inv) => (
                      <tr key={inv._id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-3">
                          {inv.email}
                          {inv.name && (
                            <span className="ml-2 text-gray-500">
                              ({inv.name})
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {inv.expired ? (
                            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                              Abgelaufen
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                              Ausstehend
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {inv.invitedBy
                            ? `${inv.invitedBy.firstName} ${inv.invitedBy.lastName}`
                            : '–'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(inv.expiresAt).toLocaleDateString('de-CH', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                          <div className="flex justify-end gap-3">
                            {inv.expired && (
                              <button
                                onClick={() =>
                                  handleResendInvitation(inv.email)
                                }
                                className="text-svisit-primary-600 hover:text-svisit-primary-900"
                              >
                                Erneut senden
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleCancelInvitation(inv._id, inv.email)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              Zurückziehen
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CompanyUsers;
